import { Component, OnInit, Input, Output, EventEmitter, inject, OnChanges, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatTableModule } from '@angular/material/table';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatSortModule, Sort } from '@angular/material/sort';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { CriticalDate } from '../../objects/critical-dates';
import { NewCriticalDateComponent } from '../../create-new/new-critical-date/new-critical-date.component';
import { UsersService } from '../../service/entity-services/users.service';
import { CriticalDatesService } from '../../service/entity-services/critical-dates.service';
import { User } from '../../objects/user';

@Component({
  selector: 'app-critical-date-connections',
  templateUrl: './critical-date-connections.component.html',
  styleUrls: ['./critical-date-connections.component.css'],
  standalone: true,
  imports: [
    CommonModule,
    MatTableModule,
    MatButtonModule,
    MatIconModule,
    MatSortModule,
    MatDialogModule,
    MatCheckboxModule
  ]
})
export class CriticalDateConnectionsComponent implements OnInit, OnChanges {

  @Input() dateObjectArray: CriticalDate[] = [];
  @Output() criticalDateUpdated = new EventEmitter<void>();

  private dialog = inject(MatDialog);
  private usersService = inject(UsersService);
  private criticalDatesService = inject(CriticalDatesService);
  private allUsers: User[] = [];

  displayedColumns: string[] = ['name', 'eventDate', 'reminderDate', 'type', 'assignedTo', 'assignedBy', 'actionCompleted', 'edit'];
  dataSource: any[] = [];

  ngOnInit(): void {
    // Subscribe to all users for hydration
    this.usersService.allUsers$.subscribe(users => {
      this.allUsers = users || [];
    });

    this.updateDataSource();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['dateObjectArray']) {
      //console.log('CriticalDateConnections dateObjectArray changed:', this.dateObjectArray?.length);
      this.updateDataSource();
    }
  }

  private updateDataSource(): void {
    // Extract unique dates and flatten the structure for table display
    const uniqueDates = Array.from(new Set(this.dateObjectArray));

    // Map to a flattened structure that's easier for the table to work with
    this.dataSource = uniqueDates.map((date: any) => ({
      raw: date, // Keep the full object for editing
      name: date.criticalDateData?.name || date.name,
      eventDate: date.criticalDateData?.eventDate || date.eventDate,
      reminderDate: date.criticalDateData?.reminderDate || date.reminderDate,
      type: date.criticalDateData?.type || date.type,
      assignedTo: date.criticalDateData?.assignedTo || date.assignedTo,
      assignedBy: date.criticalDateData?.assignedBy || date.assignedBy,
      actionCompleted: date.criticalDateData?.actionCompleted || date.actionCompleted
    }));

    // Sort by event date descending (newest first)
    this.dataSource.sort((a, b) => {
      const dateA = new Date(a.eventDate || 0).getTime();
      const dateB = new Date(b.eventDate || 0).getTime();
      return dateB - dateA;
    });

    //console.log('CriticalDateConnections dataSource:', this.dataSource.length);
  }

  formatDate(dateString: string): string {
    if (!dateString) return '';
    const date = new Date(dateString);
    const options: Intl.DateTimeFormatOptions = { year: 'numeric', month: 'short', day: 'numeric' };
    const friendlyDate = new Intl.DateTimeFormat('en-US', options).format(date);
    return friendlyDate !== 'Invalid Date' ? friendlyDate : dateString;
  }

  /**
   * Formats the assignedTo array (user emails) as comma-delimited list of names
   * Format: "LastName, FirstName; LastName, FirstName"
   */
  formatAssignedTo(userEmails: string[]): string {
    if (!userEmails || !Array.isArray(userEmails) || userEmails.length === 0) {
      return '';
    }

    const userNames = userEmails
      .map(email => this.getUserFormattedName(email))
      .filter(name => name !== ''); // Filter out any empty names

    return userNames.join('; ');
  }

  /**
   * Formats the assignedBy field (single user email) as "LastName, FirstName"
   */
  formatAssignedBy(userEmail: string): string {
    if (!userEmail) {
      return '';
    }

    return this.getUserFormattedName(userEmail);
  }

  /**
   * Helper method to get formatted user name from email
   * Format: "LastName, FirstName" or email if user not found
   */
  private getUserFormattedName(email: string): string {
    const user = this.allUsers.find(u => u.email === email);

    if (!user) {
      return email; // Fallback to email if user not found
    }

    const lastName = user.lastName || '';
    const firstName = user.firstName || '';

    if (lastName && firstName) {
      return `${lastName}, ${firstName}`;
    } else if (lastName) {
      return lastName;
    } else if (firstName) {
      return firstName;
    } else {
      return user.friendlyName || email;
    }
  }

  editCriticalDate(row: any): void {
    // Open the NewCriticalDateComponent dialog in edit mode
    const dialogRef = this.dialog.open(NewCriticalDateComponent, {
      width: '800px',
      disableClose: false,
      maxWidth: '90vw',
      maxHeight: '90vh',
      autoFocus: false,
      data: {
        title: 'Edit Critical Date',
        criticalDateData: row.raw,
        entityObject: null
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        //console.log('Critical date updated:', result);
        // Emit event to parent to refresh the critical dates list
        this.criticalDateUpdated.emit();
      }
    });
  }

  onSortChange(sort: Sort): void {
    if (!sort.active || sort.direction === '') {
      this.updateDataSource();
      return;
    }

    this.dataSource = [...this.dataSource].sort((a, b) => {
      const isAsc = sort.direction === 'asc';
      switch (sort.active) {
        case 'name':
          return this.compare(a.name, b.name, isAsc);
        case 'eventDate':
          return this.compare(new Date(a.eventDate).getTime(), new Date(b.eventDate).getTime(), isAsc);
        case 'reminderDate':
          return this.compare(new Date(a.reminderDate).getTime(), new Date(b.reminderDate).getTime(), isAsc);
        case 'type':
          return this.compare(a.type, b.type, isAsc);
        case 'assignedTo':
          return this.compare(this.formatAssignedTo(a.assignedTo), this.formatAssignedTo(b.assignedTo), isAsc);
        case 'assignedBy':
          return this.compare(this.formatAssignedBy(a.assignedBy), this.formatAssignedBy(b.assignedBy), isAsc);
        case 'actionCompleted':
          return this.compare(a.actionCompleted, b.actionCompleted, isAsc);
        default:
          return 0;
      }
    });
  }

  private compare(a: any, b: any, isAsc: boolean): number {
    return (a < b ? -1 : 1) * (isAsc ? 1 : -1);
  }
}
